import { sortBy } from 'lodash';
import React, { useMemo, useState } from 'react';
import { LuPlus } from 'react-icons/lu';

import { RestrictionType, TradeRestrictionAssetRequest } from '@ndvr/portfolio-api';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useAssetsByIdsMap } from '@/hooks/market-data-queries';
import { useRequiredParams } from 'utils';
import {
    useCreateOrUpdateTradeRestrictionAsset,
    useDeleteTradeRestrictionAsset,
    useGetPortfolioTradePreferences,
} from 'views/portfolio/account-group-queries';

import { EditRestrictionModal, StockRestriction } from './EditRestrictionModal';
import { RestrictionsTable } from './RestrictionsTable';

const useClientRestrictionsTabData = () => {
    const { accountGroupGUID } = useRequiredParams(['globalAccountGUID', 'accountGroupGUID']);
    const {
        data: preferences,
        isLoading: isLoadingPreferences,
        isFetching: isFetchingPreferences,
    } = useGetPortfolioTradePreferences(accountGroupGUID);

    const restrictionShareIds = useMemo(
        () => preferences?.restrictions?.map((r) => r.shareId) || [],
        [preferences?.restrictions],
    );

    const { data: assetsMap, isLoading: isAssetsLoading } = useAssetsByIdsMap(restrictionShareIds);

    const isLoading = isLoadingPreferences || isFetchingPreferences || isAssetsLoading;
    const sortedRestrictions =
        assetsMap.size > 0
            ? sortBy(preferences?.restrictions, (a) => assetsMap.get(a.shareId)?.symbol)
            : preferences?.restrictions;

    return {
        preferences,
        sortedRestrictions,
        assetsMap,
        isLoading,
    };
};

export const ClientRestrictionsTab = () => {
    const { preferences, sortedRestrictions, assetsMap, isLoading } = useClientRestrictionsTabData();
    const { accountGroupGUID } = useRequiredParams(['globalAccountGUID', 'accountGroupGUID']);

    const [addingRestriction, setAddingRestriction] = useState(false);

    const { mutateAsync: createOrUpdateTradeRestrictionAsset, isPending: isCreateOrUpdateTradeRestrictionAsset } =
        useCreateOrUpdateTradeRestrictionAsset(accountGroupGUID);

    const { mutateAsync: deleteTradeRestrictionAsset, isPending: isDeletingTradeRestrictionAsset } =
        useDeleteTradeRestrictionAsset(accountGroupGUID);

    const [editingStock, setEditingStock] = useState<StockRestriction | null>(null);

    const handleCreateOrUpdateTradeRestrictionAsset = async (payload: StockRestriction) => {
        const restrictionType = (() => {
            if (payload.restricted && payload.doNotSell) {
                return RestrictionType.DO_NOT_BUY_OR_SELL;
            } else if (payload.restricted) {
                return RestrictionType.DO_NOT_BUY;
            } else if (payload.doNotSell) {
                return RestrictionType.DO_NOT_SELL;
            }
            return RestrictionType.DO_NOT_BUY;
        })();

        const request: TradeRestrictionAssetRequest = {
            portfolioGUID: accountGroupGUID,
            shareId: payload.stock.shareId,
            shareClassId: payload.stock.shareClassId,
            restrictionType,
        };

        await createOrUpdateTradeRestrictionAsset({
            tradeRestriction: request,
        });
    };

    const handleEditRestriction = (shareId: string) => {
        const asset = assetsMap.get(shareId);
        const preference = sortedRestrictions?.find((item) => item.shareId === shareId);
        const info: StockRestriction = {
            stock: asset
                ? {
                      shareId: shareId,
                      shareClassId: preference?.shareClassId || '',
                      tickerSymbol: asset.symbol || '',
                      companyName: asset.description || '',
                      marketCap: '',
                      marketCapWeight: '',
                  }
                : {
                      shareId: '',
                      shareClassId: '',
                      tickerSymbol: 'N/A',
                      companyName: 'N/A',
                      marketCap: '',
                      marketCapWeight: '',
                  },
            restricted:
                preference?.restrictionType === RestrictionType.DO_NOT_BUY ||
                preference?.restrictionType === RestrictionType.DO_NOT_BUY_OR_SELL ||
                preference?.restrictionType === RestrictionType.INSIDER,
            doNotSell:
                preference?.restrictionType === RestrictionType.DO_NOT_SELL ||
                preference?.restrictionType === RestrictionType.DO_NOT_BUY_OR_SELL ||
                preference?.restrictionType === RestrictionType.INSIDER,
        };
        setEditingStock(info);
    };

    const handleDeleteRestriction = (shareId: string) => {
        deleteTradeRestrictionAsset({ shareId });
    };

    const isMutating = isCreateOrUpdateTradeRestrictionAsset || isDeletingTradeRestrictionAsset;

    return (
        <div className="space-y-4">
            <div className="mb-4">
                Apply restrictions to specific individual securities based on clients’ personal preferences or explicit
                requests.
            </div>

            <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Client Security Restrictions</h3>
                <Button
                    className="flex items-center gap-2 text-white hover:opacity-90"
                    style={{ backgroundColor: 'rgb(125, 85, 43)' }}
                    onClick={() => {
                        setAddingRestriction(true);
                    }}
                >
                    <LuPlus className="h-4 w-4" />
                    Add Restricted Security
                </Button>
            </div>
            <div className="relative">
                {isMutating && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/20 backdrop-blur-xs">
                        <div className="flex flex-col items-center gap-3">
                            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900"></div>
                            <span className="text-sm text-gray-600">Loading...</span>
                        </div>
                    </div>
                )}

                <Card>
                    <RestrictionsTable
                        restrictions={sortedRestrictions || []}
                        assetsMap={assetsMap}
                        isLoading={isLoading}
                        onEdit={handleEditRestriction}
                        onDelete={handleDeleteRestriction}
                    />
                </Card>
            </div>

            <EditRestrictionModal
                isOpen={!!editingStock || addingRestriction}
                preferences={preferences?.restrictions || []}
                onClose={() => {
                    setEditingStock(null);
                    setAddingRestriction(false);
                }}
                onSubmit={(payload) => {
                    handleCreateOrUpdateTradeRestrictionAsset(payload);
                }}
                editingStock={editingStock}
            />
        </div>
    );
};
